import React, { useEffect, useState } from "react";
import { Card, Button, Form, Modal } from "react-bootstrap";
import {
  addFNFDeductionComponent,
  getFNFDeductionComponents,
} from "../services/ResignationServices";
import Swal from "sweetalert2";

const FNFSettlementDeductions = ({
  fnfDeduction,
  setFNFDeduction,
  setFNFAmountError,
  action,
  employee,
}) => {
  const [components, setComponents] = useState([]);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [newComponent, setNewComponent] = useState("");
  const [isAddingComponent, setIsAddingComponent] = useState(false);
  const [addingComponentIndex, setAddingComponentIndex] = useState(null);
  const [loader, setLoader] = useState(false);
  const selectedComponents = fnfDeduction
    .map((d) => d.component)
    .filter(Boolean);

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        const response = await getFNFDeductionComponents();
        if (response) {
          const fetchedComponents = response.result.map((item) => ({
            key: item.id,
            value: item.component_name,
          }));
          setComponents(fetchedComponents);
        } else {
          console.error(
            "Unexpected response format for FNF deduction components",
            "response:",
            response
          );
          return;
        }
      } catch (error) {
        console.error("Error fetching FNF deduction components:", error);
        return;
      }
    };
    fetchComponents();
  }, []);

  const addFNFDeduction = () => {
    setFNFDeduction([
      ...fnfDeduction,
      { component: "", amount: "", reason: "" },
    ]);
  };

  const handleFNFDeductionChange = (index, key, value) => {
    const updatedFNFDeductions = [...fnfDeduction];

    if (key === "component" && value === "other") {
      setIsAddingComponent(true);
      setAddingComponentIndex(index);
    } else {
      setFNFAmountError(null);
      updatedFNFDeductions[index]["error"] = "";
      updatedFNFDeductions[index][key] =
        key === "component" ? Number(value) : value;
      setFNFDeduction(updatedFNFDeductions);
    }
  };

  const saveNewDeductionComponent = async (data) => {
    setLoader(true);
    try {
      const response = await addFNFDeductionComponent(data);
      if (response && response.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.message,
        });
      } else {
        Swal.fire({
          icon: "warning",
          title: "Error",
          text: "Failed to add deduction component.",
        });
      }
      return response;
    } catch (error) {
      console.error("Error saving new deduction component:", error);
      Swal.fire({
        icon: "warning",
        title: "Error",
        text: "Failed to save new deduction component.",
      });
    } finally {
      setLoader(false);
    }
  };

  const saveNewComponent = async () => {
    if (newComponent.trim() === "") {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please enter a component name.",
      });
      return;
    }

    const response = await saveNewDeductionComponent({
      component_name: newComponent,
    });

    if (!response?.success) {
      console.error("Failed to save new component.");
      return;
    }

    const updatedComponents = [
      ...components,
      { key: response?.insertId, value: newComponent },
    ];
    setComponents(updatedComponents);
    setIsAddingComponent(false);
    setNewComponent("");

    const updatedFNFDeductions = [...fnfDeduction];
    updatedFNFDeductions[addingComponentIndex].component = response?.insertId;
    setFNFDeduction(updatedFNFDeductions);
  };

  const cancelNewComponent = () => {
    setIsAddingComponent(false);
    setNewComponent("");

    const updatedFNFDeductions = [...fnfDeduction];
    updatedFNFDeductions[addingComponentIndex].component = "";
    setFNFDeduction(updatedFNFDeductions);
  };

  const confirmDelete = (index) => {
    setDeleteIndex(index);
    setShowModal(true);
  };

  const deleteFNFDeduction = () => {
    setFNFDeduction(fnfDeduction.filter((_, idx) => idx !== deleteIndex));
    setShowModal(false);
    setDeleteIndex(null);
  };

  return (
    <Card style={{ marginTop: 20, padding: 20 }}>
      <div className="d-flex justify-content-between align-items-baseline">
        <Card.Title>FNF Settlement Deductions</Card.Title>
        {!(
          action === "verifyFNFSettlement" ||
          employee?.approvalStatus !== undefined ||
          !(employee?.approvalStatus != 1)
        ) && (
          <button onClick={addFNFDeduction} className="btn btn-link me-2">
            <i
              className="fa-solid fa-plus me-2"
              title="Add new deduction section"
            ></i>
            Deduction
          </button>
        )}
      </div>
      {fnfDeduction.map((fnfDeduction, index) => (
        <div className="mb-3" key={index}>
          <div className="d-flex w-100 align-items-center">
            {/* Credit/Debit Selection */}
            <span className="me-2">{index + 1}.</span>
            {/* Component Selection */}
            {isAddingComponent && addingComponentIndex === index ? (
              <div
                className="d-flex gap-2 align-items-center me-2"
                style={{ flex: 4 }}
              >
                <Form.Control
                  type="text"
                  placeholder="Enter New Component"
                  value={newComponent}
                  onChange={(e) => setNewComponent(e.target.value)}
                />
                <Button
                  variant="success p-0 ps-1 pe-1"
                  style={{ height: "fit-content" }}
                  onClick={saveNewComponent}
                >
                  {loader ? (
                    <span className="spinner-border spinner-border-sm"></span>
                  ) : (
                    "Save"
                  )}
                </Button>
                <Button
                  variant="secondary p-0 ps-1 pe-1"
                  style={{ height: "fit-content" }}
                  onClick={cancelNewComponent}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Form.Select
                value={fnfDeduction.component}
                onChange={(e) =>
                  handleFNFDeductionChange(index, "component", e.target.value)
                }
                className="me-2"
                style={{ flex: 2 }}
                disabled={
                  action === "verifyFNFSettlement" ||
                  employee?.approvalStatus !== undefined ||
                  !(employee?.approvalStatus != 1)
                    ? true
                    : false
                }
              >
                <option value="">Select Component</option>
                {components
                  .filter(
                    (com) =>
                      !selectedComponents.includes(com.key) ||
                      fnfDeduction.component === com.key
                  )
                  .map((component, i) => (
                    <option key={i} value={component.key}>
                      {component.value}
                    </option>
                  ))}
                <option value="other">Other</option>
              </Form.Select>
            )}

            {/* Amount Input */}
            <Form.Control
              type="text"
              placeholder="Amount"
              value={fnfDeduction.amount}
              onChange={(e) =>
                handleFNFDeductionChange(index, "amount", e.target.value)
              }
              className="me-2"
              style={{ flex: 2 }}
              readOnly={
                action === "verifyFNFSettlement" ||
                employee?.approvalStatus !== undefined ||
                !(employee?.approvalStatus != 1)
                  ? true
                  : false
              }
            />

            {/* Reason Input */}
            <Form.Control
              as="textarea"
              placeholder="Reason"
              value={fnfDeduction.reason}
              onChange={(e) =>
                handleFNFDeductionChange(index, "reason", e.target.value)
              }
              className="me-2"
              rows={1}
              style={{ flex: 4 }}
              readOnly={
                action === "verifyFNFSettlement" ||
                employee?.approvalStatus !== undefined ||
                !(employee?.approvalStatus != 1)
                  ? true
                  : false
              }
            />
            {!(
              action === "verifyFNFSettlement" ||
              employee?.approvalStatus !== undefined ||
              !(employee?.approvalStatus != 1)
            ) && (
              <i
                className="fa-solid fa-trash text-danger"
                style={{ cursor: "pointer" }}
                title="Delete deduction"
                onClick={() => confirmDelete(index)}
              ></i>
            )}
          </div>
          {fnfDeduction?.error && (
            <span className="text-danger ms-3">* {fnfDeduction?.error}</span>
          )}
        </div>
      ))}
      {/* Delete Confirmation Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this deduction section?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteFNFDeduction}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default FNFSettlementDeductions;
